import { useState } from "react";
import axios from "axios";
import { Reveal } from "./Reveal";
import { Field, formatPhone, validate, validateAll, MISSING_ERROR, CONSENT_ERROR } from "./formUtils";
import { QualQuestion } from "./QualQuestion";
import { UploadZone } from "./UploadZone";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { first_name: "", last_name: "", email: "", phone: "", address: "", city: "", state: "" };
const BUREAUS = [["experian", "Experian"], ["transunion", "TransUnion"], ["equifax", "Equifax"]];

export const ApplicationForm = ({ onSubmitted }) => {
  const [v, setV] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [meets, setMeets] = useState("");
  const [qAccounts, setQAccounts] = useState(false);
  const [qLimits, setQLimits] = useState(false);
  const [consent, setConsent] = useState(false);
  const toggleConsent = (checked) => {
    setConsent(checked);
    setErrors((p) => ({ ...p, sms_consent: checked ? "" : p.sms_consent && CONSENT_ERROR }));
  };
  const [missing, setMissingRaw] = useState("");
  const pickMeets = (val) => {
    setMeets(val);
    setErrors((p) => ({ ...p, meets: "", missing: val === "no" ? p.missing : "" }));
  };
  const setMissing = (val) => {
    setMissingRaw(val);
    if (errors.missing) setErrors((p) => ({ ...p, missing: val.trim() ? "" : MISSING_ERROR }));
  };
  const [files, setFiles] = useState({});
  const [sending, setSending] = useState(false);
  const [submitErr, setSubmitErr] = useState("");

  const change = (name) => (e) => {
    let val = e.target.value;
    if (name === "phone") val = formatPhone(val);
    if (name === "state") val = val.toUpperCase().slice(0, 2);
    setV((p) => ({ ...p, [name]: val }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: validate(name, val) }));
  };
  const blur = (name) => () => setErrors((p) => ({ ...p, [name]: validate(name, v[name]) }));
  const bind = (name) => ({ value: v[name], onChange: change(name), onBlur: blur(name), error: errors[name] });

  const submit = async (e) => {
    e.preventDefault();
    const errs = validateAll(v, meets, missing, consent);
    setErrors(errs);
    if (Object.keys(errs).length) {
      document.getElementById(Object.keys(errs)[0])?.focus();
      return;
    }
    setSending(true);
    setSubmitErr("");
    const fd = new FormData();
    Object.entries(v).forEach(([k, val]) => fd.append(k, val));
    fd.append("meets_criteria", meets);
    fd.append("missing_criteria", meets === "no" ? missing.trim() : "");
    fd.append("sms_consent", consent);
    fd.append("q_accounts", meets === "no" && qAccounts);
    fd.append("q_limits", meets === "no" && qLimits);
    Object.entries(files).forEach(([k, f]) => f && fd.append(k, f));
    try {
      await axios.post(`${API}/applications`, fd);
      onSubmitted(v.first_name.trim());
    } catch (err) {
      setSubmitErr(err?.response?.data?.detail || "Something went wrong submitting your application. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Reveal className="card" id="form-section" data-testid="application-card">
      <div className="section-label">Application</div>
      <h2>Apply for the Pay It Forward Program</h2>
      <form onSubmit={submit} noValidate data-testid="application-form">
        <div className="form-grid">
          <Field id="first_name" label="First Name" placeholder="First name" autoComplete="given-name" {...bind("first_name")} />
          <Field id="last_name" label="Last Name" placeholder="Last name" autoComplete="family-name" {...bind("last_name")} />
          <Field id="email" type="email" className="full" label="Email Address" placeholder="you@example.com" autoComplete="email" {...bind("email")} />
          <Field id="phone" type="tel" className="full" label="Phone Number" placeholder="(000) 000-0000" autoComplete="tel" inputMode="tel" {...bind("phone")} />
          <Field id="address" className="full" label="Street Address" placeholder="123 Main Street" autoComplete="street-address" {...bind("address")} />
          <Field id="city" label="City" placeholder="City" autoComplete="address-level2" {...bind("city")} />
          <Field id="state" label="State" placeholder="FL" autoComplete="address-level1" maxLength={2} {...bind("state")} />
        </div>
        <hr className="divider" />
        <QualQuestion {...{ meets, setMeets: pickMeets, qAccounts, setQAccounts, qLimits, setQLimits, missing, setMissing, errors }} />
        <hr className="divider" />
        <div className="upload-head">
          <h3>Upload Your Credit Reports</h3>
          <p>Upload your most recent report from each bureau. PDF or image accepted. Upload what you have — we will work with it.</p>
        </div>
        <div className="upload-grid">
          {BUREAUS.map(([id, name]) => (
            <UploadZone key={id} id={id} bureau={name} file={files[id]} onFile={(f) => setFiles((p) => ({ ...p, [id]: f }))} />
          ))}
        </div>
        <hr className="divider" />
        <div className="consent-wrap">
          <label className="check-opt consent-opt" htmlFor="sms_consent">
            <input type="checkbox" id="sms_consent" checked={consent} onChange={(e) => toggleConsent(e.target.checked)}
              aria-invalid={!!errors.sms_consent} data-testid="sms-consent-checkbox" />
            <span>I agree to receive SMS messages from Iron Gate Consulting.</span>
          </label>
          {errors.sms_consent && <div className="field-error" data-testid="error-sms_consent">{errors.sms_consent}</div>}
        </div>
        <div className="submit-wrap">
          <button type="submit" className="btn-submit" disabled={sending} data-testid="submit-application-button">
            <span>{sending ? "Submitting…" : "Submit My Application"}</span>
          </button>
          {Object.values(errors).some(Boolean) && <p className="submit-error" data-testid="form-error-summary">Please fix the highlighted fields above before submitting.</p>}
          {submitErr && <p className="submit-error" data-testid="submit-error">{submitErr}</p>}
          <p className="submit-note">Once submitted, <strong>someone from our team will call you within the hour</strong> to go over your options. No payment required at any stage.</p>
        </div>
      </form>
    </Reveal>
  );
};
